import { Component, Input, ElementRef, EventEmitter, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';

import { ApiError, AuthService, MessageService } from '../../../core';
import { CollapseGroupComponent } from '../../../shared';
import { AtividadeChecklistItem } from './atividade-checklist-item';
import { AtividadeChecklistItemCalculoService, AtividadeChecklistItemAuthGuardService, AtividadeChecklistItemHttpService } from './services';
import { take } from 'rxjs/operators';

@Component({
    selector: 'sgt-atividade-checklist-item',
    templateUrl: './atividade-checklist-item.component.html',
    styleUrls: ['./atividade-checklist-item.component.less']
})
export class AtividadeChecklistItemComponent {
    @Input() idAtividade: number;
    @Input() entities: AtividadeChecklistItem[];
    @Input() quantidadeChecklistItens: number;
    @Output() quantidadeChecklistItensChange = new EventEmitter<number>();
    @Input() quantidadeChecklistItensConcluidos: number;
    @Output() quantidadeChecklistItensConcluidosChange = new EventEmitter<number>();
    @Input() percQuantidadeChecklistItensConcluidos: number;
    @Output() percQuantidadeChecklistItensConcluidosChange = new EventEmitter<number>();

    /** Configs. */
    @Input() canInsertManual = true; // Se pode inserir manualmente um item no checklist
    /** Se deve persistir as alterações */
    @Input() autoPersist = false;

    @ViewChild('collapseGroup') collapseGroup: CollapseGroupComponent;
    @ViewChildren('textareaNome') textareasNome: QueryList<ElementRef>;

    constructor(private authService: AuthService,
                private messageService: MessageService,
                private atividadeChecklistItemAuthGuardService: AtividadeChecklistItemAuthGuardService,
                private atividadeChecklistItemCalculoService: AtividadeChecklistItemCalculoService,
                private atividadeChecklistItemHttpService: AtividadeChecklistItemHttpService) { }

    /** Calcula as quantidade de itens, itens concluídos e percentual de itens concluídos dos itens do checklist da atividade */
    private calcularQuantidadesChecklistItens() {
        // Quantidade de itens do checklist
        this.quantidadeChecklistItens = this.entities ? this.entities.length : 0;
        this.quantidadeChecklistItensChange.emit(this.quantidadeChecklistItens);

        // Quantidade de itens concluídos do checklist
        this.quantidadeChecklistItensConcluidos = this.atividadeChecklistItemCalculoService.calcularQuantidadeChecklistItensConcluidos(this.entities);
        this.quantidadeChecklistItensConcluidosChange.emit(this.quantidadeChecklistItensConcluidos);

        // Percentual de itens concluídos do checklist
        this.percQuantidadeChecklistItensConcluidos = this.atividadeChecklistItemCalculoService.calcularPercQuantidadeChecklistItensConcluidos(this.quantidadeChecklistItensConcluidos, this.quantidadeChecklistItens);
        this.percQuantidadeChecklistItensConcluidosChange.emit(this.percQuantidadeChecklistItensConcluidos);
    }

    /** Verifica se o item do checklist possui alguma opção disponível */
    hasOptions(entity: AtividadeChecklistItem): boolean {
        return this.canDelete(entity);
    }

    /** Verifica se pode editar o item do checklist da atividade */
    canEdit(entity: AtividadeChecklistItem): boolean {
        return this.atividadeChecklistItemAuthGuardService.canEdit(entity.lancamento);
    }

    /** Verifica se pode inserir itens no checklist da atividade */
    canInsert(): boolean {
        return this.atividadeChecklistItemAuthGuardService.canInsert(this.canInsertManual);
    }

    /** Insere um novo item no checklist */
    insert() {
        if (!this.canInsert()) {
            return ;
        }

        if (!this.entities) {
            this.entities = []
        };

        const entity = new AtividadeChecklistItem(this.idAtividade);
        entity.sequencia = this.entities.length == 0 ? 1 : this.entities[this.entities.length - 1].sequencia + 1;
        this.entities?.push(entity);
        this.calcularQuantidadesChecklistItens();

        this.collapseGroup?.open()
            .then(() => {
                setTimeout(() => this.setFocusTextareaNome(), 200);
            })
            .catch(() => {});
    }

    /** Ao sair do campo do nome */
    async onBlurNome(entity: AtividadeChecklistItem, index: number) {
        try {
            if (!entity) {
                return;
            }

            // Realizando a persistencia quando assim for desejado
            if (this.autoPersist) {
                let isPersisted = false;
                if (!entity.id && entity.nome) { // Insert
                    entity = await this.persistInsert(entity);
                    isPersisted = true;
                } else if (entity.id) { // Update
                    entity = await this.persistUpdate(entity);
                    isPersisted = true;
                }

                if (isPersisted) {
                    this.entities[index] = entity;
                }
            }
        } catch (error) {
            //
        }
    }

    /** Persiste o insert de um novo item no checklist */
    private persistInsert(entity: AtividadeChecklistItem): Promise<AtividadeChecklistItem> {
        return new Promise<AtividadeChecklistItem>((resolve, reject) => {
            this.atividadeChecklistItemHttpService.insert(entity)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => resolve(response),
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao inserir o item do checklist da atividade', error);
                        reject(error);
                    }
                );
        });
    }

    /** Persiste a atualização de um item no checklist */
    private persistUpdate(entity: AtividadeChecklistItem): Promise<AtividadeChecklistItem> {
        return new Promise<AtividadeChecklistItem>((resolve, reject) => {
            this.atividadeChecklistItemHttpService.update(entity)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => resolve(response),
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao atualizar o item do checklist da atividade', error);
                        reject(error);
                    }
                );
        });
    }

    /** Verifica se pode excluir a atividade */
    canDelete(entity: AtividadeChecklistItem): boolean {
        return this.atividadeChecklistItemAuthGuardService.canDelete(entity.lancamento);
    }

    /** Persiste a exclusão do item do checklist */
    private persistDelete(entity: AtividadeChecklistItem): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.atividadeChecklistItemHttpService.delete(entity.idAtividade, entity.id)
                .pipe(
                    take(1)
                )
                .subscribe(
                    () => resolve(),
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Exclui um item do checklist */
    async delete(entity: AtividadeChecklistItem, index: number) {
        try {
            if (!this.canDelete(entity)) {
                return;
            }

            if (index > -1) {
                if (this.autoPersist) {
                    await this.persistDelete(entity);
                }

                this.entities?.splice(index, 1);
                this.calcularQuantidadesChecklistItens();
            }
        } catch (error) {
            this.messageService.error(error.message, 'Erro ao excluir o item do checklist da atividade', error);
        }
    }

    /** Seta o foco no textarea do nome ao inserir um novo item */
    private setFocusTextareaNome() {
        // Obtendo o último textarea para setar o foco
        const textarea = this.textareasNome?.last;
        jQuery(textarea?.nativeElement).trigger('focus');
    }

    /** Realiza a persistencia da atualização da conclusão do item do checklist */
    private persistUpdateConcluido(entity: AtividadeChecklistItem): Promise<AtividadeChecklistItem> {
        return new Promise<AtividadeChecklistItem>((resolve, reject) => {
            this.atividadeChecklistItemHttpService.updateConcluido(entity.idAtividade, entity.id, entity.concluido)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => resolve(response),
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Ao alterar se o item está concluído ou não */
    async onChangeConcluido(entity: AtividadeChecklistItem) {
        try {
            if (this.autoPersist) {
                entity = await this.persistUpdateConcluido(entity);
            } else {
                if (entity.concluido) {
                    entity.dataConclusao = new Date();
                    entity.usuarioConclusao = this.authService.getUsuario();
                    entity.idUsuarioConclusao = entity.usuarioConclusao?.id;
                } else {
                    entity.dataConclusao = null;
                    entity.idUsuarioConclusao = null;
                    entity.usuarioConclusao = null;
                }
            }

            this.calcularQuantidadesChecklistItens();
        } catch (error) {
            this.messageService.error(error.message, 'Erro ao atualizar a conclusão do item do checklist', error);
            entity.concluido = !entity.concluido;
        }
    }

    /** Executa validações e métodos antes de salvar os itens do checklist da atividade */
    beforeSave() {
        // Removendo os itens do array que não possuirem o nome informado
        for (let i = this.entities?.length; i--; ) {
            const entity = this.entities[i];
            if (!entity.nome || entity.nome.trim().length === 0) {
                this.entities.splice(i, 1);
            }
        }

        // Setando as sequências dos itens
        this.setSequencias();
    }

    /** Seta as sequências dos itens conforme a sua ordem no array */
    private setSequencias() {
        let sequencia = 1;
        this.entities?.forEach(entity => {
            entity.sequencia = sequencia;
            sequencia++;
        });
    }
}
